import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Sun, Moon, Menu, X, User as UserIcon, LayoutDashboard, FileText, Tag,
  Bookmark, Bell, Settings, HelpCircle, LogOut, Briefcase, Shield, ChevronDown,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/app/NotificationBell";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/notes", label: "Available Notes" },
  { to: "/portfolios", label: "Portfolios" },
  { to: "/originations", label: "Originations" },
  { to: "/applications", label: "Pre-Approve", authOnly: true },
  { to: "/deals", label: "My Deals", authOnly: true },
  { to: "/sell/new", label: "Sell", authOnly: true },
  { to: "/how-it-works", label: "How It Works" },
];

/**
 * Account menu — same items across desktop dropdown and mobile drawer.
 * Items marked `comingSoon` route to /coming-soon for now.
 * Items marked `roles` only appear when the effective user role matches.
 */
const buildAccountMenu = (role) => [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard,
    to: role === "admin" ? "/admin" : role === "dealer" ? "/dealer" : "/dashboard" },
  { key: "profile", label: "My Profile", icon: UserIcon, to: "/profile" },
  // role-specific entries
  ...(role === "user" || role === "dealer" ? [
    { key: "applications", label: "My Applications", icon: FileText, to: "/dashboard?tab=app" },
    { key: "offers", label: "My Offers", icon: Tag, to: "/dashboard?tab=offers" },
    { key: "saved", label: "Saved Notes", icon: Bookmark, to: "/dashboard?tab=saved" },
  ] : []),
  ...(role === "dealer" ? [
    { key: "listings", label: "My Listings", icon: Briefcase, to: "/dealer?tab=listings" },
  ] : []),
  ...(role === "admin" ? [
    { key: "imp-logs", label: "Impersonation Audit", icon: Shield, to: "/admin/impersonation-logs" },
  ] : []),
  { key: "notifications", label: "Notifications", icon: Bell, to: "/notifications" },
  { key: "wallet", label: "AI Wallet", icon: Tag, to: "/wallet" },
  { key: "settings", label: "Settings", icon: Settings, to: "/profile?tab=preferences" },
  { key: "help", label: "Help", icon: HelpCircle, to: "/how-it-works" },
];


export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // close mobile drawer on navigation
  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  const accountMenu = user ? buildAccountMenu(user.role) : [];

  return (
    <header data-testid="navbar" className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary text-primary-foreground flex items-center justify-center font-display text-lg">F</div>
          <span className="font-display text-xl tracking-tight">FinanceLocal</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.filter((n) => !n.authOnly || !!user).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide hover:text-primary transition-colors ${isActive ? "text-primary font-medium" : "text-foreground/80"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button data-testid="theme-toggle" onClick={toggle} aria-label="Toggle theme"
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-muted">
            {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger"
                  className="flex items-center gap-2 px-3 h-9 border border-border hover:bg-muted">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-semibold uppercase">
                      {(user.name || user.email || "?").trim().charAt(0)}
                    </div>
                  )}
                  <span className="text-sm max-w-[150px] truncate">{user.name || user.email}</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-64">
                <DropdownMenuLabel className="font-overline text-xs">
                  {user.role} · {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {accountMenu.map((m) => {
                  const Icon = m.icon;
                  return (
                    <DropdownMenuItem
                      key={m.key}
                      data-testid={`menu-${m.key}`}
                      onClick={() => navigate(m.to)}
                      className="cursor-pointer flex items-center gap-2"
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="flex-1">{m.label}</span>
                      {m.comingSoon && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
                          Soon
                        </span>
                      )}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}
                  className="cursor-pointer flex items-center gap-2 text-red-600 dark:text-red-400">
                  <LogOut className="w-3.5 h-3.5" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button data-testid="navbar-login" variant="outline" className="rounded-none uppercase tracking-wider text-xs"
                onClick={() => navigate("/login")}>Login</Button>
              <Button data-testid="navbar-signup" className="rounded-none uppercase tracking-wider text-xs"
                onClick={() => navigate("/login?signup=1")}>Create Account</Button>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-1.5">
          {user && <NotificationBell />}
          <button data-testid="mobile-menu-toggle" className="p-2 -mr-2"
            onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-border bg-background">
          <div className="px-4 py-3 divide-y divide-border">
            {/* Section 1 — public nav */}
            <div className="pb-3 space-y-1">
              {NAV.filter((n) => !n.authOnly || !!user).map((n) => (
                <NavLink key={n.to} to={n.to} data-testid={`mobile-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block py-2 text-sm ${isActive ? "text-primary font-medium" : "text-foreground/80"}`}>
                  {n.label}
                </NavLink>
              ))}
            </div>

            {/* Section 2 — account (only if signed in) */}
            {user && (
              <div className="py-3 space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-overline">
                  Account · {user.role}
                </div>
                {accountMenu.map((m) => {
                  const Icon = m.icon;
                  return (
                    <Link key={m.key} to={m.to} data-testid={`mobile-menu-${m.key}`}
                      className="flex items-center gap-2 py-2 text-sm hover:text-primary">
                      <Icon className="w-4 h-4" />
                      <span className="flex-1">{m.label}</span>
                      {m.comingSoon && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
                          Soon
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Section 3 — auth + theme */}
            <div className="pt-3 flex items-center gap-2">
              <button data-testid="mobile-theme-toggle" onClick={toggle}
                className="w-9 h-9 border border-border flex items-center justify-center">
                {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </button>
              {user ? (
                <Button data-testid="mobile-logout" onClick={logout} variant="outline"
                  className="rounded-none flex-1 text-red-600 border-red-300 dark:text-red-400 dark:border-red-800">
                  <LogOut className="w-4 h-4 mr-1.5" /> Logout
                </Button>
              ) : (
                <>
                  <Button data-testid="mobile-login" onClick={() => navigate("/login")}
                    variant="outline" className="rounded-none flex-1">Login</Button>
                  <Button data-testid="mobile-signup" onClick={() => navigate("/login?signup=1")}
                    className="rounded-none flex-1">Sign up</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
